<template>
  <div class="skills-workspace">
    <div class="skills-container">
      <header class="skills-hero">
        <div>
          <div class="eyebrow">SKILL ORCHESTRATION</div>
          <h2>技能工作台</h2>
          <p>
            为 OpenClaw 挂载可审计、可卸载的工具能力，安装前先做公开信息查验。
          </p>
        </div>
        <div class="hero-stat">
          <span class="stat-number">{{ installedSkills.length }}</span>
          <span class="stat-label">已挂载技能</span>
        </div>
      </header>

      <section class="search-panel">
        <div class="search-copy">
          <strong>安装新技能</strong>
          <span>输入 ClawHub 技能名称，例如 weather、calendar、browser。</span>
        </div>
        <el-input
          v-model="skillNameToInstall"
          placeholder="weather"
          class="skill-search-input"
          clearable
          @keyup.enter="handleSearchSkill"
        >
          <template #append>
            <el-button
              :loading="isSearchingSkill"
              @click="handleSearchSkill"
              type="primary"
            >
              <el-icon><Search /></el-icon> 查验并安装
            </el-button>
          </template>
        </el-input>
      </section>

      <div class="skills-grid-wrapper" v-loading="isFetchingSkills">
        <el-empty
          v-if="installedSkills.length === 0 && !isFetchingSkills"
          description="您的工作区空空如也，快去安装第一个技能吧！"
        />
        <div class="skills-grid" v-else>
          <div
            class="skill-card"
            v-for="skill in installedSkills"
            :key="skill.id"
          >
            <div class="skill-icon-box">
              <span class="skill-icon">{{ getSkillEmoji(skill.name) }}</span>
            </div>
            <div class="skill-info">
              <div class="skill-title-row">
                <span class="skill-name">{{ skill.name }}</span>
                <el-tag size="small" type="success" effect="plain"
                  >Installed</el-tag
                >
              </div>
              <div v-if="skill.author" class="skill-author">
                作者：{{ skill.author }}
              </div>
              <el-tooltip
                :content="skill.description"
                placement="top"
                :show-after="500"
              >
                <div class="skill-desc">{{ skill.description }}</div>
              </el-tooltip>
            </div>
            <div class="skill-delete-action">
              <el-button
                type="danger"
                circle
                plain
                size="small"
                @click="handleDeleteSkill(skill.slug)"
              >
                <el-icon><Delete /></el-icon>
              </el-button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <el-dialog
      v-model="infoDialogVisible"
      title="🛡️ 技能安全查验"
      width="650px"
      align-center
    >
      <div class="info-dialog-tip">
        以下是从 ClawHub 抓取的候选技能，请选择作者并核实内容：
        <div
          v-for="skill in currentSkillInfo.skills"
          :key="skill.slug"
          class="skill-candidate"
          :class="{ selected: selectedSkill?.slug === skill.slug }"
          @click="selectedSkill = skill"
        >
          <div class="candidate-main">
            <strong>{{ skill.name }}</strong>
            <span>作者：{{ skill.author }}</span>
          </div>

          <div class="candidate-meta">
            <span>{{ skill.description }}</span>
            <span class="candidate-installs">{{ skill.installs }} 次安装</span>
          </div>
        </div>
      </div>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="infoDialogVisible = false">取消安装</el-button>
          <el-button
            type="primary"
            :disabled="!selectedSkill"
            @click="executeBackgroundInstall"
          >
            确认安装
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import axios from "axios";
import { ref, onMounted } from "vue";
import { Search, Delete } from "@element-plus/icons-vue";
import { ElMessageBox, ElMessage, ElNotification } from "element-plus";

const API_BASE_URL = "/api";
const currentUserId = ref("user_888"); // 临时硬编码

const installedSkills = ref([]);
const isFetchingSkills = ref(false);
const skillNameToInstall = ref("");
const isSearchingSkill = ref(false);

const infoDialogVisible = ref(false);
const currentSkillInfo = ref({
  query: "",
  skills: [],
  raw_info: "",
});
const selectedSkill = ref(null);

const getSkillEmoji = (name) => {
  const emojis = ["🧩", "⚡", "🌐", "🛠️", "💡", "📅", "☁️", "🤖", "🚀", "📊"];
  if (!name) return "🧩";
  const index = name.charCodeAt(0) % emojis.length;
  return emojis[index];
};

const fetchInstalledSkills = async () => {
  isFetchingSkills.value = true;
  try {
    const res = await axios.get(`${API_BASE_URL}/skills`);
    installedSkills.value = res.data;
  } catch (error) {
    ElMessage.error(error.response?.data?.detail || "获取技能列表失败");
  } finally {
    isFetchingSkills.value = false;
  }
};

const handleSearchSkill = async () => {
  const query = skillNameToInstall.value.trim();
  if (!query) return;

  isSearchingSkill.value = true;
  try {
    const res = await axios.get(`${API_BASE_URL}/skills/info`, {
      params: {
        q: query,
      },
    });
    currentSkillInfo.value = res.data;
    selectedSkill.value = null;
    infoDialogVisible.value = true;
  } catch (error) {
    ElMessage.warning(error.response?.data?.detail || `未找到技能 ${query}`);
  } finally {
    isSearchingSkill.value = false;
  }
};

// 局部封装：用于在安装/卸载技能后，向后端发送新建对话请求以刷新大模型记忆
const createSessionSilently = async (title) => {
  try {
    await axios.post(`${API_BASE_URL}/sessions`, {
      title: title,
      user_id: currentUserId.value,
    });
  } catch (error) {
    console.error("静默刷新会话失败:", error);
  }
};

const executeBackgroundInstall = async () => {
  if (!selectedSkill.value) {
    ElMessage.warning("请先选择一个技能");
    return;
  }

  const skillSlug = selectedSkill.value.slug;
  infoDialogVisible.value = false;
  skillNameToInstall.value = "";

  const notification = ElNotification({
    title: "静默安装中...",
    message: `正在后台为您拉取安装 ${skillSlug}，您可以离开此页面进行其他操作。`,
    type: "info",
    duration: 0,
  });

  try {
    await axios.post(`${API_BASE_URL}/skills/install`, {
      slug: skillSlug,
    });
    notification.close();
    ElNotification({
      title: "安装成功！🎉",
      message: `技能 ${skillSlug} 已准备就绪。`,
      type: "success",
      duration: 5000,
    });
    fetchInstalledSkills();
    await createSessionSilently(`系统已挂载新技能: ${skillSlug}`);
  } catch (error) {
    notification.close();
    ElNotification({
      title: "安装失败 ❌",
      message: error.response?.data?.detail || "未知错误",
      type: "error",
      duration: 8000,
    });
  }
};

const handleDeleteSkill = (skillSlug) => {
  ElMessageBox.confirm(
    `确定要卸载技能 "${skillSlug}" 吗？该操作将物理删除底层依赖。`,
    "🚨 危险卸载警告",
    {
      confirmButtonText: "确认卸载",
      cancelButtonText: "手滑了",
      type: "warning",
      confirmButtonClass: "el-button--danger",
    },
  )
    .then(async () => {
      try {
        await axios.delete(`${API_BASE_URL}/skills`, {
          params: { slug: skillSlug },
        });
        ElMessage.success(`技能 ${skillSlug} 已彻底卸载！`);
        fetchInstalledSkills();
        await createSessionSilently(`系统已卸载技能: ${skillSlug}`);
      } catch (error) {
        ElMessage.error(error.response?.data?.detail || "卸载失败");
      }
    })
    .catch(() => {});
};

onMounted(() => {
  fetchInstalledSkills();
});
</script>

<style scoped>
.skills-workspace {
  --panel-border: #dce6f2;
  --ink: #203040;
  --muted: #6d7b8a;
  padding: 36px 48px 56px;
  background: radial-gradient(
      circle at 15% 10%,
      rgba(64, 158, 255, 0.12),
      transparent 28%
    ),
    linear-gradient(135deg, #f6f9fc 0%, #eef4f8 100%);
  overflow-y: auto;
  height: 100%;
}
.skills-container {
  max-width: 1320px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
}
.skills-hero {
  display: flex;
  justify-content: space-between;
  align-items: stretch;
  gap: 28px;
  padding: 30px 34px;
  border: 1px solid var(--panel-border);
  border-radius: 24px;
  background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.94),
      rgba(241, 247, 252, 0.88)
    ),
    repeating-linear-gradient(
      90deg,
      transparent 0 23px,
      rgba(32, 48, 64, 0.035) 24px
    );
  box-shadow: 0 24px 60px rgba(55, 84, 112, 0.12);
}
.eyebrow {
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.16em;
  color: #409eff;
  margin-bottom: 10px;
}
.skills-hero h2 {
  font-size: 34px;
  line-height: 1;
  color: var(--ink);
  margin: 0 0 12px;
  letter-spacing: -0.04em;
}
.skills-hero p {
  color: var(--muted);
  font-size: 15px;
  margin: 0;
  max-width: 620px;
}
.hero-stat {
  min-width: 180px;
  border-radius: 20px;
  background: #203040;
  color: #fff;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.12);
}
.stat-number {
  font-size: 46px;
  font-weight: 900;
  line-height: 1;
}
.stat-label {
  margin-top: 8px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.72);
}
.search-panel {
  display: grid;
  grid-template-columns: 260px minmax(360px, 1fr);
  gap: 24px;
  align-items: center;
  padding: 22px 24px;
  background: rgba(255, 255, 255, 0.88);
  border: 1px solid var(--panel-border);
  border-radius: 20px;
  box-shadow: 0 16px 38px rgba(55, 84, 112, 0.09);
}
.search-copy {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.search-copy strong {
  color: var(--ink);
  font-size: 16px;
}
.search-copy span {
  color: var(--muted);
  font-size: 13px;
}
.skill-search-input :deep(.el-input-group__append) {
  background-color: #203040;
  color: white;
  border-color: #203040;
}
.skill-search-input :deep(.el-input-group__append:hover) {
  background-color: #31465d;
}
.skills-grid-wrapper {
  min-height: 360px;
  padding: 8px 0;
}
.skills-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(330px, 1fr));
  gap: 20px;
}
.skill-card {
  display: flex;
  align-items: flex-start;
  min-height: 116px;
  padding: 22px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid var(--panel-border);
  border-radius: 20px;
  position: relative;
  box-shadow: 0 14px 34px rgba(55, 84, 112, 0.08);
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}
.skill-card:hover {
  box-shadow: 0 22px 46px rgba(55, 84, 112, 0.16);
  transform: translateY(-4px);
  border-color: #b8cbe0;
}
.skill-icon-box {
  margin-right: 16px;
  background: linear-gradient(135deg, #eff7ff, #dcecff);
  width: 54px;
  height: 54px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.skill-icon {
  font-size: 30px;
}
.skill-info {
  flex: 1;
  overflow: hidden;
}
.skill-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}
.skill-name {
  font-weight: bold;
  font-size: 17px;
  color: var(--ink);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.skill-desc {
  font-size: 13px;
  color: var(--muted);
  line-height: 1.65;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
}
.skill-delete-action {
  position: absolute;
  top: -10px;
  right: -10px;
  opacity: 0;
  transition: 0.2s;
}
.skill-card:hover .skill-delete-action {
  opacity: 1;
  top: 10px;
  right: 10px;
}
.info-dialog-tip {
  margin-bottom: 15px;
  color: #606266;
  font-size: 14px;
}
.skill-author {
  margin-bottom: 4px;
  color: #7a8a9a;
  font-size: 12px;
}
.skill-candidate {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 12px;
  margin: 8px 0;
  border: 1px solid #dce6f2;
  border-radius: 10px;
  cursor: pointer;
}
.candidate-main {
  min-width: 0;
  flex: 1;
}
.candidate-meta {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  min-width: 170px;
  text-align: right;
}
.candidate-meta > span:first-child {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.candidate-installs {
  flex: 0 0 auto;
  min-width: 64px;
  text-align: right;
  white-space: nowrap;
}
.skill-candidate:hover,
.skill-candidate.selected {
  border-color: #409eff;
  background: #ecf5ff;
}
.skill-candidate span {
  display: block;
  margin-top: 4px;
  color: #6d7b8a;
  font-size: 12px;
}
@media (max-width: 640px) {
  .skill-candidate {
    align-items: flex-start;
  }
  .candidate-meta {
    display: block;
    min-width: 96px;
  }
  .candidate-meta > span:first-child {
    white-space: normal;
  }
  .candidate-installs {
    margin-top: 6px;
  }
}
.terminal-box {
  background: #111b26;
  color: #b9c7d8;
  padding: 18px;
  border-radius: 14px;
  max-height: 350px;
  overflow-y: auto;
  font-family: "Fira Code", "Courier New", Courier, monospace;
  font-size: 13px;
  line-height: 1.6;
}
.terminal-box pre {
  margin: 0;
  white-space: pre-wrap;
  word-wrap: break-word;
}
@media (max-width: 900px) {
  .skills-workspace {
    padding: 24px;
  }
  .skills-hero,
  .search-panel {
    grid-template-columns: 1fr;
    flex-direction: column;
  }
  .hero-stat {
    min-height: 120px;
  }
}
</style>
